import {debounce, difference, findKey, throttle, uniq} from 'lodash';
import memoizeOne from 'memoize-one';
import React, {Component, PropsWithChildren} from 'react';
import {connect} from 'react-redux';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {TaskSourcesEnum} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {
  buildCancelTokenSource,
  CancelTokenSource,
} from '../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {makeMapStateToProps, wrapInShallowSelector} from '../../../srcCommon/helpers/reselectHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../../srcCommon/services/logger/logger';
import {AblySocket} from '../../../srcCommon/services/push/ably';
import {PresenceMember, PushSocket} from '../../../srcCommon/services/push/pushSocket';
import {inAnimationFrameOrHiddenCallbackAsync} from '../../helpers/async/inAnimationFrame';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {CompanyModel} from '../../models/api/companyModel';
import {PushConfigModel, PushConnectorsEnum} from '../../models/api/pushConfigModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {info} from '../../services/debug';
import {postAsync} from '../../services/http/http';
import {
  createChatUserOnlineStatusAction,
  deleteChatUserOnlineStatusAction,
} from '../../store/data/chat/chatUserOnlineActions';
import {deleteChatPrecog, updateChatPrecog} from '../../store/data/chatPrecogs/chatPrecogsActions';
import {getCompany} from '../../store/data/company/companySelectors';
import {getChatPushConfig} from '../../store/data/dataSelectors';
import {getUser} from '../../store/data/teammates/teammatesSelectors';
import {getViewerPanelsChatConversationIds} from '../../store/panels/viewer/viewerPanelSelectors';
import {ChatPushContext} from './chatPushContext';

/*
 * Props.
 */

interface ChatPushStateProps {
  chatConfig?: PushConfigModel;
  company: CompanyModel;
  user: TeammateModel;
  viewerPanelsConversationIds: ReadonlyArray<number>;
}

interface ChatPushProps extends ChatPushStateProps, ResolveDispatchProps<typeof dispatchProps> {}

/*
 * Types.
 */

export interface OptimisticMessageAuthor {
  id: number;
  name: string;
  avatarUrl: string;
}

export interface OptimisticMessage {
  uid: string;
  conversationId: number;
  createdAt: number;
  sentAt: number;
  text: string;
  author: OptimisticMessageAuthor;
}

/*
 * Component.
 */

type Unsubscribe = () => void;

// Max time to wait before disconnecting from the Pusher chat instance when no chat conversation is open.
// We do this so as to avoid unnecessary connections when it's not used.
const inactivityGracePeriod = 5 * 60 * 1000;

// Max time after which a precog is deleted if no continue message is received.
const chatPrecogTimeout = 5000;

const chatPushLogger = logger.getLogger('chat-push');

interface AblyAuthorizationResponse {
  authentication: string;
  rooms: ReadonlyArray<{
    room_name: string;
    conversation_id: number;
    front_chat_conversation_id: string;
  }>;
}

class ChatPush extends Component<PropsWithChildren<ChatPushProps>> {
  constructor(props: PropsWithChildren<ChatPushProps>) {
    super(props);

    const {chatConfig} = this.props;

    if (!chatConfig) {
      return;
    }

    this.chatSocket = this.makeChatPushSocket(chatConfig);
  }

  // We don't want to keep a Pusher chat connection open for nothing if the
  // user is not actively interacting with Front chat conversations.
  private disconnectDelayed = throttle(
    () => {
      if (this.isConnected) {
        this.chatSocket!.disconnect();
        this.isConnected = false;
      }

      this.isActive = false;
    },
    inactivityGracePeriod,
    {leading: false, trailing: true},
  );

  private isActive = false;
  private isConnected = false;
  private ablyAuthToken?: string;
  private lastCancelTokenSource: CancelTokenSource | null = null;

  private readonly chatSocket: PushSocket | undefined;

  private conversationChatMap: {[conversationId: number]: {chatId: string; roomName: string}} = {};
  private conversationPresenceConnections: {[conversationId: number]: Set<string>} = {};

  // Map containing delayed functions that delete expired precogs.
  private deleteChatPrecogDebouncedMap: {[conversationId: number]: () => void} = {};

  deleteChatPrecogDebounced(conversationId: number) {
    // Create a debounced precog delete function for this conversationId if it doesn't exist.
    if (!(conversationId in this.deleteChatPrecogDebouncedMap)) {
      this.deleteChatPrecogDebouncedMap[conversationId] = debounce(
        () =>
          inAnimationFrameOrHiddenCallbackAsync(
            () => {
              this.props.deleteChatPrecog(conversationId);
              // This throttled chat precog deletion has triggered, we don't need it anymore now.
              delete this.deleteChatPrecogDebouncedMap[conversationId];
            },
            {source: TaskSourcesEnum.PUSHER},
          ),
        chatPrecogTimeout,
        {leading: false, trailing: true},
      );
    }

    // Schedule/bump the delayed execution.
    this.deleteChatPrecogDebouncedMap[conversationId]();
  }

  /*
   * Lifecycle.
   */

  componentDidMount() {
    if (!this.chatSocket) {
      return;
    }

    globalWindow.addEventListener('offline', () => {
      if (this.isConnected) {
        this.chatSocket!.disconnect();
        this.isConnected = false;
      }
    });
    globalWindow.addEventListener('online', () => {
      this.updateConnectionStatus();
    });

    // Register lifecycle events.
    this.chatSocket.on('connected', async () => {
      info('Chat push connected.');
      this.resubscribeToRooms();
    });

    this.chatSocket.on('disconnected', async () => {
      info('Chat push disconnected.');
      this.unsubscribeFromRooms();
    });

    this.isActive = this.props.viewerPanelsConversationIds.length > 0;

    // Maybe connect the socket.
    if (navigator.onLine && this.isActive) {
      this.connectSocket();
    }
  }

  componentDidUpdate() {
    if (!this.chatSocket) {
      return;
    }

    this.updateConnectionStatus();

    if (this.isActive && this.isConnected) {
      this.resubscribeToRooms();
    }
  }

  private updateConnectionStatus() {
    const hasSubscriptions = this.props.viewerPanelsConversationIds.length > 0;
    if (hasSubscriptions) {
      this.isActive = true;
      this.disconnectDelayed.cancel();
      // Schedule a disconnect in the future
    } else if (this.isActive) {
      this.disconnectDelayed();
    }

    if (!this.isConnected && this.isActive && navigator.onLine) {
      this.connectSocket();
    }
  }

  componentWillUnmount() {
    if (!this.chatSocket) {
      return;
    }

    this.unsubscribeFromRooms();

    // Cleanup the socket.
    this.chatSocket.removeAllListeners();
    this.chatSocket.disconnect();
  }

  private makeChatPushSocket(config: PushConfigModel): PushSocket | undefined {
    // The Front Chat widget only supports the Ably connector. The widget does not
    // support Socket.IO, so we we do not fallback to Socket.IO in interop mode.
    // PB-39048: Introduce Socket.IO to the Front Chat widget for interop development.
    if (config.connector !== PushConnectorsEnum.ABLY) {
      return undefined;
    }

    const ablyAuthorizer = async (rooms: ReadonlyArray<string>) => {
      const authorizedRooms = Object.values(this.conversationChatMap).map((obj) => obj.roomName);

      const unauthorizedRooms = difference(rooms, authorizedRooms);
      if (unauthorizedRooms.length > 0) {
        throw new Error(`Unexpected to have unauthorized rooms: ${unauthorizedRooms}`);
      }

      if (!this.ablyAuthToken) {
        throw new Error('Ably authorizer invoked before authToken was set');
      }

      return this.ablyAuthToken;
    };

    return new AblySocket(
      config.roomsPrefix,
      config.environment,
      false,
      () => this.props.company,
      () => true,
      this.props.user.id,
      {shouldUsePrivateRoomPrefix: false, defaultRooms: []},
      ablyAuthorizer,
    );
  }

  private connectSocket() {
    // Connect the driver.
    this.chatSocket!.connect();
    this.isConnected = true;

    this.chatSocket!.onDirectPushEvent('chat_precog_updated', (data) => {
      const chatConversationId = data.chat_conversation_id;
      const conversationId: string | undefined = findKey(this.conversationChatMap, [
        'chatId',
        chatConversationId,
      ]);
      if (!conversationId) {
        return;
      }

      inAnimationFrameOrHiddenCallbackAsync(
        () => {
          this.props.updateChatPrecog(Number(conversationId));
        },
        {source: TaskSourcesEnum.PUSHER},
      );

      this.deleteChatPrecogDebounced(Number(conversationId));
    });

    this.resubscribeToRooms();
  }

  /*
   * Subscriptions.
   */

  private async resubscribeToRooms() {
    const conversationIds = this.props.viewerPanelsConversationIds;
    return this.updateSubscriptions(conversationIds);
  }

  private unsubscribeFromRooms() {
    return this.updateSubscriptions([]);
  }

  private subscriptions: {[conversationId: number]: Unsubscribe} = {};

  // TODO: We need to investigate this further since on first connect we will
  // likely double call this from componentDidUpdate. The first call won't be
  // back in time and we'll end up getting the auth call cancelled.
  private async updateSubscriptions(conversationIds: ReadonlyArray<number>) {
    // Get the previous and next set of conversationIds we want to be subscribed to.
    const previousConversations = Object.keys(this.subscriptions).map(Number);
    const nextConversations = uniq(conversationIds);

    // Compute the difference.
    const subscribeToConversations = difference(nextConversations, previousConversations);
    const unsubscribeFromConversations = difference(previousConversations, nextConversations);

    // Unsubscribe.
    unsubscribeFromConversations.forEach((conversationId) => {
      try {
        if (conversationId in this.conversationChatMap) {
          delete this.conversationChatMap[conversationId];
        }

        const unsubscribe = this.subscriptions[conversationId];
        if (!unsubscribe) {
          return;
        }

        // Try to unsubscribe.
        unsubscribe();
      } catch (error) {
        reportToBugsnag(new ExtendedError(`Failed to unsubscribe from chat room '${conversationId}'`, error));
      } finally {
        // Remove from the subscriptions no matter what.
        delete this.subscriptions[conversationId];

        if (this.chatSocket?.replaceSubscriptions) {
          const currentConversationIds = Object.keys(this.subscriptions).map(Number);
          this.chatSocket.replaceSubscriptions(
            currentConversationIds.map((id) => this.conversationChatMap[id].roomName),
          );
        }
      }
    });

    // Early exit if there are no chat channels to subscribe to.
    if (nextConversations.length === 0) {
      return;
    }

    if (this.lastCancelTokenSource) {
      this.lastCancelTokenSource.cancel();
    }
    this.lastCancelTokenSource = buildCancelTokenSource();

    // Fetch authToken for all rooms we'll be subscribed to.
    const result: AblyAuthorizationResponse = await postAsync(
      `${this.props.company.url}/ably/auth_chat`,
      {
        conversation_ids: nextConversations,
      },
      {cancelToken: this.lastCancelTokenSource.token},
    );

    // Update ablyAuthToken and chatSocket subscriptions.
    // NOTE: Both of these operations must occur _before_
    // chatSocket.subscribe is called below.
    this.ablyAuthToken = result.authentication;
    const authenticatedRooms = result.rooms.map((room) => room.room_name);
    if (this.chatSocket?.replaceSubscriptions) {
      this.chatSocket.replaceSubscriptions(authenticatedRooms);
    }

    const roomsToSubscribeTo = subscribeToConversations.map((conversationId) =>
      result.rooms.find((room) => room.conversation_id === conversationId),
    );

    for (const room of roomsToSubscribeTo) {
      const chatId = room!.front_chat_conversation_id;
      const roomName = room!.room_name;
      const conversationId = room!.conversation_id;

      this.conversationChatMap[conversationId] = {roomName, chatId};
      const subscribeCallbacks = this.chatSocket!.subscribe(roomName);
      this.subscriptions[conversationId] = subscribeCallbacks.unsubscribe;

      if (!('getPresence' in subscribeCallbacks)) {
        throw new Error(`Expected to receive presence callbacks for chat channel: ${roomName}`);
      }
      const {getPresence, onPresenceEnter, onPresenceExit} = subscribeCallbacks;

      // Initialize presence state.
      getPresence((err: Error | null, members?: ReadonlyArray<PresenceMember>) => {
        if (err) {
          chatPushLogger.error('Received error fetching initial presence state', {
            conversationId,
            error: JSON.stringify(err),
          });
          return;
        }

        for (const member of members ?? []) {
          this.conversationPresenceConnections[conversationId] ||= new Set();
          this.conversationPresenceConnections[conversationId].add(toPresenceMemberIdentifier(member));
        }

        if (members && members.length > 0) {
          this.props.createChatUserOnlineStatusAction(conversationId);
        }
      });

      // Register presence listeners.
      onPresenceEnter((member: PresenceMember) => {
        const id = toPresenceMemberIdentifier(member);
        this.conversationPresenceConnections[conversationId] ||= new Set();

        // If its their first time entering the room we need to enable the
        // online status.
        if (!this.conversationPresenceConnections[conversationId].has(id)) {
          this.props.createChatUserOnlineStatusAction(conversationId);
        }

        this.conversationPresenceConnections[conversationId].add(id);
      });

      onPresenceExit((member: PresenceMember) => {
        const id = toPresenceMemberIdentifier(member);
        const presenceMembersSet = this.conversationPresenceConnections[conversationId];
        if (!presenceMembersSet || !presenceMembersSet.has(id)) {
          chatPushLogger.warn('Unknown user received from presence event', {
            conversationId,
            clientId: member.clientId,
            connectionId: member.connectionId,
          });
          return;
        }

        // If the last client is leaving, then we need to remove the presence
        // indicator.
        if (presenceMembersSet.size === 1) {
          delete this.conversationPresenceConnections[conversationId];
          this.props.deleteChatUserOnlineStatusAction(conversationId);
          return;
        }

        presenceMembersSet.delete(id);
      });
    }
  }

  /*
   * Context.
   */

  private readonly buildContextValue = memoizeOne(() => ({
    emitChatPrecog: (conversationId: number, data: any) =>
      this.chatSocket &&
      this.chatSocket.emitDirectEvent(
        this.conversationChatMap[conversationId]!.roomName,
        'chat_reply_precog_updated',
        data,
      ),
    emitChatOptimisticMessage: (conversationId: number, messageData: OptimisticMessage) => {
      // It is sometimes possible for this emitter to be called before subscribing to a conversation or
      // after unsubscribing. This is because we keep rolling array of room subscriptions which updates
      // as the user selects different conversations in P2, and message send actions are queued separately.
      //
      // In the case where the conversation room is not ready, the real message will still go through via
      // Front's own queue infrastructure which processes the messages and delivers it to the chat widget.
      if (!this.chatSocket || !this.conversationChatMap[conversationId]?.roomName) {
        return;
      }

      this.chatSocket.emitDirectEvent(
        this.conversationChatMap[conversationId].roomName,
        'optimistic_message_sent',
        messageData,
      );
    },
  }));

  /*
   * Render.
   */

  render() {
    const {children} = this.props;
    const contextValue = this.buildContextValue();
    return <ChatPushContext.Provider value={contextValue}>{children}</ChatPushContext.Provider>;
  }
}

/*
 * Helpers.
 */

function toPresenceMemberIdentifier(member: PresenceMember) {
  return `${member.clientId}-${member.connectionId}`;
}

/*
 * Redux connection.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  chatConfig: getChatPushConfig,
  company: getCompany,
  user: getUser,
  viewerPanelsConversationIds: wrapInShallowSelector(getViewerPanelsChatConversationIds),
}));

const dispatchProps = {
  updateChatPrecog,
  deleteChatPrecog,
  createChatUserOnlineStatusAction,
  deleteChatUserOnlineStatusAction,
};

export const ChatPushContainer = connect(mapStateToProps, dispatchProps)(ChatPush);
